import type { FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";

export interface MarketVenue {
  id: string;
  name: string;
  nameEn: string;
  type: "night_market" | "fresh_market" | "community_market";
  operatingDays: number[];
  openHour: number;
  closeHour: number;
  isOpenNow: boolean;
  vendorCount: number;
  location: [number, number];
  highlights: string[];
}

export interface OtopProduct {
  name: string;
  category: "food" | "beverage" | "herb" | "textile" | "craft";
  producer: string;
  starRating: 1 | 2 | 3 | 4 | 5;
}

export interface CommunityEnterprise {
  name: string;
  type: string;
  memberCount: number;
  product: string;
}

export interface LocalMarketData {
  venues: MarketVenue[];
  otopProducts: OtopProduct[];
  communityEnterprises: CommunityEnterprise[];
  totalActiveVendors: number;
  totalOtopRegistered: number;
  source: string;
  observedAt: string;
}

interface Props {
  data: LocalMarketData | null;
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

const DAY_NAMES_TH = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];

const VENUE_TYPE_LABEL: Record<MarketVenue["type"], string> = {
  night_market: "ตลาดนัด",
  fresh_market: "ตลาดสด",
  community_market: "OTOP",
};

const CATEGORY_LABEL: Record<OtopProduct["category"], string> = {
  food: "FOOD",
  beverage: "BEV",
  herb: "HERB",
  textile: "TEX",
  craft: "ART",
};

export function LocalMarketPanel({ data, loading, ageMinutes, fallbackTier }: Props) {
  if (loading && !data) {
    return (
      <div className="col">
        <div className="eyebrow">ตลาด / MARKETS</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
      </div>
    );
  }
  if (!data) return null;

  const openVenues = data.venues.filter((v) => v.isOpenNow);

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="ตลาดชุมชน "
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
        source="otop·เทศบาล"
      />

      {/* Summary row */}
      <div style={{ display: "flex", gap: 12, marginBottom: 2 }}>
        <div>
          <div className="mono" style={{ fontSize: "1.1rem", lineHeight: 1, color: openVenues.length > 0 ? "var(--good)" : "var(--text-3)" }}>
            {openVenues.length > 0 ? data.totalActiveVendors : "—"}
          </div>
          <div className="eyebrow" style={{ color: "var(--text-3)" }}>
            {openVenues.length > 0 ? "แผงเปิดอยู่" : "ปิดทั้งหมด"}
          </div>
        </div>
        <div>
          <div className="mono" style={{ fontSize: "1.1rem", lineHeight: 1 }}>
            {data.totalOtopRegistered}
          </div>
          <div className="eyebrow" style={{ color: "var(--text-3)" }}>OTOP</div>
        </div>
        <div>
          <div className="mono" style={{ fontSize: "1.1rem", lineHeight: 1 }}>
            {data.communityEnterprises.length}
          </div>
          <div className="eyebrow" style={{ color: "var(--text-3)" }}>วิสาหกิจ</div>
        </div>
      </div>

      {/* Venues */}
      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 4 }}>
        {data.venues.map((venue) => (
          <div key={venue.id} style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
            <span
              className="eyebrow"
              style={{
                background: venue.isOpenNow ? "var(--good)" : "var(--bg-3)",
                color: venue.isOpenNow ? "var(--fixed-light)" : "var(--text-3)",
                padding: "1px 4px",
                minWidth: 32,
                textAlign: "center",
              }}
            >
              {venue.isOpenNow ? "เปิด" : "ปิด"}
            </span>
            <span className="caption" style={{ flex: 1, color: venue.isOpenNow ? "var(--text)" : "var(--text-3)" }}>
              {venue.name}
              {/* Venue type — ตลาดนัด / ตลาดสด / OTOP. The map existed but
                  was never rendered, so a night market and a fresh market
                  looked identical in this list. Mirrors how the OTOP row
                  below shows CATEGORY_LABEL. */}
              <span className="mono" style={{ color: "var(--text-3)", marginLeft: 6, fontSize: "0.85em" }}>
                {VENUE_TYPE_LABEL[venue.type]}
              </span>
            </span>
            <span className="mono caption" style={{ color: "var(--text-3)" }}>
              {venue.operatingDays.map((d) => DAY_NAMES_TH[d]).join("/")}
              {" "}{venue.openHour}–{venue.closeHour}น.
            </span>
          </div>
        ))}
      </div>

      {/* OTOP highlights */}
      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 4 }}>
        <div className="eyebrow" style={{ color: "var(--text-3)", marginBottom: 4 }}>OTOP · ผลิตภัณฑ์เด่น</div>
        {data.otopProducts.slice(0, 4).map((p) => (
          <div key={p.name} style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 2 }}>
            <span className="mono eyebrow" style={{ color: "var(--text-3)", minWidth: 32 }}>{CATEGORY_LABEL[p.category]}</span>
            <span className="caption" style={{ flex: 1 }}>{p.name}</span>
            <span className="mono caption" style={{ color: "var(--text-3)" }}>
              {"★".repeat(p.starRating)}
            </span>
          </div>
        ))}
      </div>

      {/* Community enterprises */}
      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 4 }}>
        <div className="eyebrow" style={{ color: "var(--text-3)", marginBottom: 4 }}>วิสาหกิจชุมชน</div>
        {data.communityEnterprises.slice(0, 3).map((ce) => (
          <div key={ce.name} style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 2 }}>
            <span className="caption" style={{ flex: 1 }}>{ce.product}</span>
            <span className="mono caption" style={{ color: "var(--text-3)" }}>{ce.memberCount} คน</span>
          </div>
        ))}
      </div>
    </div>
  );
}
